import { ChangeEvent, useContext, useState } from 'react';
import Flatpickr from 'react-flatpickr';
import { toast } from 'react-toastify';
import {
    ContractorsAPI,
    EmployeesAPI,
    WorkshiftsSchedulingAPI,
} from '../../../../services/external/APIs';
import ToolsBar from '../../controller-modals/tools-bar/ToolsBar';

import { AppContext } from '../../../../common';
import { ACTIONS } from '../../../../constants';
import {
    ContractorModel,
    DownloadReportsModel,
    EmployeeNodeModel,
} from '../../../../models';
import {
    getChildNodes,
    getEmployeesIds,
    getMonths,
    handlerFormatDate,
} from '../../../../utils';
import { getActionService } from '../../../../utils/getActionService';
import { BaseConfirmModal } from '../../modals';
import { WorkshiftMultiSelector } from '../../selectors/Workshifts/WorkshiftMultiSelector';
import { CostCenterMenuGroup } from './groups-reports/CostCenterMenuGroup';
import { MarkingMenuGroup } from './groups-reports/MarkingMenuGroup';
import { NoveltiesMenuGroup } from './groups-reports/NoveltiesMenuGroup';
import OperationCenterMenuGroup from './groups-reports/OperationCenterMenuGroup';
import { RequestMenuGroup } from './groups-reports/RequestMenuGroup';
import { WorkshiftMenuGroup } from './groups-reports/WorkshiftMenuGroup';
import './menuBar.css';
import { BusinessUnitMenuGroup } from './groups-reports/BusinessUnitMenuGroup';
import { PreliquidationsMenuGroup } from './groups-reports/PreliquidationsMenuGroup';

interface Props {
    typeOfPerson: string;
    startDateScheduling: string;
    endDateScheduling: string;
    selectedEmployees: EmployeeNodeModel[];
    contractorsList: ContractorModel[];
    setTreeEmployeesCopy: (nweState: EmployeeNodeModel[]) => void;
    setTreeEmployees: (nweState: EmployeeNodeModel[]) => void;
    setSelectedEmployees: (nweState: EmployeeNodeModel[]) => void;
    setTypeOfPerson: (newState: string) => void;
    setStartDateScheduling: (newState: string) => void;
    setEndDateScheduling: (newState: string) => void;
    setEmployeesToShowTable: (newState: EmployeeNodeModel[]) => void;
    setContractorSelected: (newState: ContractorModel | undefined) => void;
    setIsOpenFilterUnattendanceInterruptionsReportModal: (newState: boolean) => void;
    setIsOpenFilterInterruptionsRepotModal: (newState: boolean) => void;
    setIsOpenFilterWorkingSchedulingsRepotModal: (newState: boolean) => void;
    setIsOpenFilterLunchTimeControl: (newState: boolean) => void;
    setIsOpenFilterDailyAssistance: (newState: boolean) => void;
    setGroupList: (newState: EmployeeNodeModel[]) => void;
}

/**
 * El menu sirve para envolver toda la parte superior de menu de herramientas, tanto la parte de seleccionar el tipo de empleado, fechas todo el componente del menu de acciones y los reportes
 * Pero esta enfocado en toda la parte del reporte y contiene las funciones que se ejecutan al momento de darle click algun reporte
 * @param Props
 * @returns
 */
const MenuBar = ({
    typeOfPerson,
    startDateScheduling,
    endDateScheduling,
    selectedEmployees,
    contractorsList,
    setTypeOfPerson,
    setTreeEmployeesCopy,
    setTreeEmployees,
    setEndDateScheduling,
    setStartDateScheduling,
    setEmployeesToShowTable,
    setContractorSelected,
    setIsOpenFilterUnattendanceInterruptionsReportModal,
    setIsOpenFilterDailyAssistance,
    setIsOpenFilterInterruptionsRepotModal,
    setIsOpenFilterLunchTimeControl,
    setIsOpenFilterWorkingSchedulingsRepotModal,
    setSelectedEmployees,
    setGroupList,
}: Props) => {
    const [isOpenFilteredWorkshift, setIsOpenFilteredWorkshift] = useState(false);
    const [workshiftsToFilter, setWorkshiftsToFilter] = useState([]);

    const {
        langResources,
        lang,
        userInfo,
        isSkinSmart,
        isCollapseTree,
        setIsCollapseTree,
        handleConsultScheduling,
        downloadReport,
        handlerOpenAdditionalHours,
        handlerOpenAidTypeModal,
        handlerOpenCostCenterModal,
        handlerOpenDeleteWorkshiftModal,
        handlerOpenDisassociatedCostCenterModal,
        handlerOpenInteruptionModal,
        handlerOpenUpdateAndDeleteAdditionalHoursModal,
        handlerOpenUpdateAndDeleteInterruptionsModal,
        handlerOpenWorkshiftModal,
        handlerOpenBonusModal,
        saveExceptionalWorkshiftsScheduling,
        handlerDisassociatedBusinessUnitsModal,
        handlerCopyWorkshiftsSchedulings,
        handlerSavePasteWorkshiftsSchedulings,
        handlerBusinessUnitsModal,
        handlerOpenAssociateMarkingModal,
        handlerOpenModifyMarkingModal,
        handlerValidateNovelties,
        handlerOpenMultipleCostCentersByHourRangesModal,
    } = useContext(AppContext);

    /**
     * Cambia las fechas inicial y final
     * @param dates
     */
    const handlerChangeDate = (dates: Date[]) => {
        if (!dates || dates.length < 2) {
            return;
        }

        let startDate = handlerFormatDate(dates[0]);
        let endDate = handlerFormatDate(dates[1]);

        if (startDate) setStartDateScheduling(startDate);
        if (endDate) setEndDateScheduling(endDate);
    };

    /**
     * Cambia el tipo de persona
     * @param  { target }: ChangeEvent<HTMLSelectElement>
     */
    const handlerChangeTypeOfPerson = ({
        target,
    }: ChangeEvent<HTMLSelectElement>) => {
        setTypeOfPerson(target.value);
    };

    /**
     * Se encarga de actualizar la tabla de que empleados se van a mostrar
     */
    const updateEmplooyesTable = (workshiftIds: number[]) => {
        setEmployeesToShowTable(selectedEmployees);
        handleConsultScheduling(
            selectedEmployees,
            startDateScheduling,
            endDateScheduling,
            true,
            workshiftIds
        );
    };

    /**
     * Descarga el reporte por periodos tiempos
     */
    const handlerSaveReportByPeriod = (
        period: number,
        periodName: string,
        name: string,
        url: string
    ) => {
        let body: DownloadReportsModel = {
            startDate: startDateScheduling,
            employeeIds: getEmployeesIds(selectedEmployees),
            periodType: period,
        };

        if (
            body.startDate !== '' &&
            body.employeeIds &&
            body.employeeIds.length > 0
        ) {
            downloadReport(
                body,
                undefined,
                undefined,
                undefined,
                langResources['reports.message.alert'],
                `${langResources['report.label']} ${periodName} ${name}`,
                '/jerusalem/api/v2.0.0/reports/' + url
            );
        } else {
            toast.error(langResources['reports.message.alert'], {
                isLoading: false,
                autoClose: 5000,
                position: toast.POSITION.BOTTOM_RIGHT,
                closeOnClick: true,
                closeButton: true,
            });
        }
    };

    /**
     * Esta función permite cambiar el contrato y obtenemos los empleados basado en ese contrato
     * @param { target }: ChangeEvent<HTMLSelectElement>
     */
    const handleChangeContractorSelected = ({
        target,
    }: ChangeEvent<HTMLSelectElement>) => {
        let contractorId = Number(target.value);

        let contractorSelected = contractorsList.find((c) => c.id == contractorId);

        if (contractorSelected?.id) {
            ContractorsAPI.getEmployeeByContractors(contractorSelected.id)
                .then((res: EmployeeNodeModel[]) => {
                    setTreeEmployeesCopy(res);
                    setTreeEmployees(res);

                    setGroupList(
                        res.filter((emp) => emp.droppable || emp.parent === 0)
                    );

                    if (res.length == 0) {
                        toast.warning(
                            langResources['tree.filter.employees.by.contract.alert'],
                            {
                                isLoading: false,
                                autoClose: 5000,
                                position: toast.POSITION.BOTTOM_RIGHT,
                                closeOnClick: true,
                                closeButton: true,
                            }
                        );
                    }
                })
                .catch((err) =>
                    console.log('Filter Employees By Contract', err.response.data)
                );
        } else {
            EmployeesAPI.getContractors().then((response) => {
                setTreeEmployees(response);
                setTreeEmployeesCopy(response);
                setSelectedEmployees([]);
            });
        }

        setContractorSelected(contractorSelected);
    };

    /**
     * Validaciones de los excepcionales si hay dias no compensados, tema de liquidacion
     */
    const handlerValidateWorkshiftsExceptionals = () => {
        let body = {
            startDate: startDateScheduling + 'T01:00',
            endDate: endDateScheduling + 'T01:00',
            employeeIds: getEmployeesIds(selectedEmployees),
        };

        if (
            body.startDate !== '' &&
            body.endDate !== '' &&
            body.employeeIds &&
            body.employeeIds.length > 0
        ) {
            let id = toast.loading(
                langResources['validate.exceptional.workshift']['loading']
            );

            WorkshiftsSchedulingAPI.validateWorkshiftsExceptionals(body)
                .then((res) => {
                    toast.update(id, {
                        render: langResources['validate.exceptional.workshift'][
                            'success.request'
                        ],
                        type: 'success',
                        isLoading: false,
                        autoClose: 5000,
                        position: toast.POSITION.BOTTOM_RIGHT,
                        closeOnClick: true,
                        closeButton: true,
                    });
                    handleConsultScheduling(
                        selectedEmployees,
                        startDateScheduling,
                        endDateScheduling,
                        true,
                        []
                    );
                })
                .catch((err) => {
                    toast.update(id, {
                        render: langResources['validate.exceptional.workshift'][
                            'error.request'
                        ],
                        type: 'error',
                        isLoading: false,
                        autoClose: 5000,
                        position: toast.POSITION.BOTTOM_RIGHT,
                        closeOnClick: true,
                        closeButton: true,
                    });
                });
        }
    };

    const onChange = (workshiftsList: any) => {
        setWorkshiftsToFilter(workshiftsList);
    };

    /**
     * ASIGNAMOS DISPONIBILIDAD A EMPLEADOS
     */
    const saveWorkshiftsSundaysUsuals = () => {
        const id = toast.loading('validando dominicales habituales...');

        let body = {
            startDate: startDateScheduling,
            endDate: endDateScheduling,
            employeeIds: getEmployeesIds(selectedEmployees),
        };

        if (
            (body.endDate !== '',
            body.startDate !== '' || body.employeeIds.length > 0)
        ) {
            WorkshiftsSchedulingAPI.validateWorkshiftsSundaysUsuals(body)
                .then((res) => {
                    toast.update(id, {
                        render: 'Dominicales habituales validados exitosamente',
                        type: 'success',
                        isLoading: false,
                        autoClose: 5000,
                        position: toast.POSITION.BOTTOM_RIGHT,
                        closeOnClick: true,
                        closeButton: true,
                    });
                    handleConsultScheduling(
                        selectedEmployees,
                        startDateScheduling,
                        endDateScheduling,
                        true,
                        []
                    );
                })
                .catch((err) => {
                    toast.update(id, {
                        render: err.response
                            ? err.response.data
                            : 'Ocurrió un error al realizar la validación',
                        type: 'error',
                        isLoading: false,
                        autoClose: 5000,
                        position: toast.POSITION.BOTTOM_RIGHT,
                        closeOnClick: true,
                        closeButton: true,
                    });
                });
        } else {
            toast.update(id, {
                render: 'Selecciona un rango de fechas y/o los empledos',
                type: 'error',
                isLoading: false,
                autoClose: 5000,
                position: toast.POSITION.BOTTOM_RIGHT,
                closeOnClick: true,
                closeButton: true,
            });
        }
    };

    return (
        <>
            <BaseConfirmModal
                isLoading={false}
                modalTitle={'Consultar La Programación'}
                onClose={() => {
                    setIsOpenFilteredWorkshift(false);
                    setWorkshiftsToFilter([]);
                    //@ts-ignore
                    document.getElementById('multi-selector-workshifts').value = '';
                }}
                onSubmit={() => {
                    setIsOpenFilteredWorkshift(false);
                    setWorkshiftsToFilter([]);
                    // @ts-ignore
                    document.getElementById(
                        'btn-consulting'
                        // @ts-ignore
                    ).disabled = true;
                    // @ts-ignore

                    document.getElementById('multi-selector-workshifts').value = '';

                    updateEmplooyesTable(workshiftsToFilter);
                    setIsCollapseTree(true);
                    setTimeout(() => {
                        // @ts-ignore
                        let item = document.getElementById(
                            // @ts-ignore
                            'btn-consulting'
                        );
                        if (item) {
                            // @ts-ignore
                            item.disabled = false;
                        }
                    }, 5000);
                }}
                isOpen={isOpenFilteredWorkshift}
                isShowControls={true}
                textButton={langResources['basemodal.save.button']}
            >
                <div className='modal-body text-dark p-0 m-0'>
                    <WorkshiftMultiSelector
                        onChange={onChange}
                        value={workshiftsToFilter}
                    />
                </div>
            </BaseConfirmModal>

            <ul
                className={`navbar-ctions ${
                    !isCollapseTree ? 'navbar-ctions--collapse' : ''
                } row mb-2`}
            >
                <li
                    className='navbar__actions-wrapper'
                    style={{ backgroundColor: '#fff' }}
                >
                    <div className=' row align-items-center' style={{ height: 55 }}>
                        <div
                            id='navbar-actions-small'
                            className={`col-12 col-lg-6  ${
                                typeOfPerson === 'contractors'
                                    ? 'col-xl-6 col-xxl-4'
                                    : 'col-xl-4 '
                            } d-flex gap-2 gap-lg-3 align-items-center justify-content-evenly  justify-content-md-start justify-content-lg-start pt-2 pt-lg-0`}
                        >
                            <div className='d-flex gap-2 align-items-center'>
                                <select
                                    name='options'
                                    className={`form-select text-dark pe-2 py-1 select__control ${
                                        typeOfPerson === 'contractors'
                                            ? 'select__control--inactive'
                                            : ''
                                    }`}
                                    onChange={(e) => handlerChangeTypeOfPerson(e)}
                                >
                                    <option value='default'>
                                        {langResources['header.type.employees']}
                                    </option>
                                    <option value='contractors'>
                                        {langResources['header.type.contractors']}
                                    </option>
                                    <option value='all-employees'>
                                        {langResources['header.type.all']}
                                    </option>
                                </select>

                                {typeOfPerson === 'contractors' && (
                                    <select
                                        name='contractors'
                                        className='form-select text-dark pe-2 py-1 select__control'
                                        onChange={(e) => {
                                            handleChangeContractorSelected(e);
                                            setEmployeesToShowTable([]);
                                            setSelectedEmployees([]);
                                            setEndDateScheduling('');
                                            setStartDateScheduling('');
                                        }}
                                        title={
                                            langResources[
                                                'header.type.default.placeholder'
                                            ] +
                                            ' ' +
                                            langResources['header.type.contractors']
                                        }
                                    >
                                        <option
                                            defaultValue={
                                                langResources[
                                                    'header.type.contractors'
                                                ]
                                            }
                                            value={-1}
                                            key={-1}
                                        >
                                            <span className='d-lg-none'> -- --</span>
                                        </option>
                                        {contractorsList.map((c) => (
                                            <option
                                                value={c.id}
                                                key={c.id}
                                                title={c.commercialName}
                                            >
                                                {c.commercialName.toLowerCase()}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>

                            <div className='d-flex'>
                                <div className='date__picker'>
                                    <Flatpickr
                                        lang='es'
                                        title={
                                            startDateScheduling +
                                            ' to ' +
                                            endDateScheduling
                                        }
                                        id='picker-date'
                                        className='form-control picker text-black select__control'
                                        options={{
                                            mode: 'range',
                                            dateFormat: 'Y-m-d',
                                            locale: {
                                                months: getMonths(lang),
                                            },
                                        }}
                                        onChange={(date) => {
                                            handlerChangeDate(date);
                                        }}
                                        value={
                                            startDateScheduling +
                                            ' to ' +
                                            endDateScheduling
                                        }
                                        placeholder='YYYY-MM-DD'
                                    />
                                    <label htmlFor='picker-date'>
                                        <i className='mdi mdi-calendar-blank fs-4 text-muted'></i>
                                    </label>
                                </div>
                            </div>

                            {getActionService(
                                userInfo.actions,
                                ACTIONS['search.scheduling']
                            ) && (
                                <div className='btn-group'>
                                    <button
                                        style={{
                                            height: 30,
                                            fontSize: 11,
                                            zIndex: 999,
                                        }}
                                        title={langResources['header.consult.btn']}
                                        type='button'
                                        id='btn-consulting'
                                        className='btn btn-primary d-flex align-items-center  text-center'
                                        onClick={(e) => {
                                            // @ts-ignore
                                            document.getElementById(
                                                'btn-consulting'
                                                // @ts-ignore
                                            ).disabled = true;

                                            updateEmplooyesTable([]);
                                            setIsCollapseTree(true);
                                            setTimeout(() => {
                                                // @ts-ignore
                                                let item = document.getElementById(
                                                    // @ts-ignore
                                                    'btn-consulting'
                                                );
                                                if (item) {
                                                    // @ts-ignore
                                                    item.disabled = false;
                                                }
                                            }, 5000);
                                        }}
                                        disabled={
                                            startDateScheduling === '' ||
                                            endDateScheduling === '' ||
                                            getChildNodes(selectedEmployees)
                                                .length == 0 ||
                                            selectedEmployees.length === 0
                                        }
                                    >
                                        <i className='ri-search-line d-md-none'></i>
                                        {langResources[
                                            'header.consult.btn'
                                        ].toUpperCase()}
                                    </button>
                                    <button
                                        style={{
                                            height: 30,
                                            fontSize: 11,
                                            zIndex: 999,
                                        }}
                                        type='button'
                                        className='btn btn-primary d-flex align-items-center  text-center dropdown-toggle dropdown-toggle-split'
                                        data-bs-toggle='dropdown'
                                        aria-expanded='false'
                                        disabled={
                                            startDateScheduling === '' ||
                                            endDateScheduling === '' ||
                                            getChildNodes(selectedEmployees)
                                                .length == 0 ||
                                            selectedEmployees.length === 0
                                        }
                                    >
                                        <span className='visually-hidden'>
                                            Toggle Dropdown
                                        </span>
                                    </button>
                                    <ul className='dropdown-menu'>
                                        <li
                                            onClick={() => {
                                                setIsOpenFilteredWorkshift(
                                                    !isOpenFilteredWorkshift
                                                );
                                            }}
                                        >
                                            <a className='dropdown-item'>
                                                Filtrar por turno
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            )}
                        </div>

                        <div
                            className={`d-none d-lg-flex col-lg-6  ${
                                typeOfPerson === 'contractors'
                                    ? 'col-xl-6 col-xxl-8'
                                    : 'col-xl-8 '
                            } align-items-center justify-content-end`}
                        >
                            <ToolsBar
                                startDate={startDateScheduling}
                                endDate={endDateScheduling}
                                treeEmployees={selectedEmployees}
                                openWorkshiftModal={handlerOpenWorkshiftModal}
                                openBonusModal={handlerOpenBonusModal}
                                openDeleteWorkshiftModal={
                                    handlerOpenDeleteWorkshiftModal
                                }
                                openUpdateAndDeleteInterruptionsModal={
                                    handlerOpenUpdateAndDeleteInterruptionsModal
                                }
                                openUpdateAndDeleteAdditionalHoursModal={
                                    handlerOpenUpdateAndDeleteAdditionalHoursModal
                                }
                                openInterruptionModal={handlerOpenInteruptionModal}
                                openAdditionalHours={handlerOpenAdditionalHours}
                                openCostCenterModal={handlerOpenCostCenterModal}
                                openMultipleCostCentersByHourRangesModal={
                                    handlerOpenMultipleCostCentersByHourRangesModal
                                }
                                saveExceptionalWorkshiftsScheduling={
                                    saveExceptionalWorkshiftsScheduling
                                }
                                openBusinessUnitsModal={handlerBusinessUnitsModal}
                                openAidTypesModal={handlerOpenAidTypeModal}
                                openDisassociatedBusinessUnitsModal={
                                    handlerDisassociatedBusinessUnitsModal
                                }
                                openDisassociatedCostCenterModal={
                                    handlerOpenDisassociatedCostCenterModal
                                }
                                openCopyWorkshiftsSchedulings={
                                    handlerCopyWorkshiftsSchedulings
                                }
                                openSavePasteWorkshiftsSchedulings={
                                    handlerSavePasteWorkshiftsSchedulings
                                }
                                handlerValidateWorkshiftsExceptionals={
                                    handlerValidateWorkshiftsExceptionals
                                }
                                saveWorkshiftsSundaysUsuals={
                                    saveWorkshiftsSundaysUsuals
                                }
                                handlerValidateNovelties={handlerValidateNovelties}
                                openAssociateTimeAttendanceEvents={
                                    handlerOpenAssociateMarkingModal
                                }
                                modifyAssociateTimeAttendanceEvents={
                                    handlerOpenModifyMarkingModal
                                }
                            />

                            <div className='scollbar '>
                                <div className='container-fluid px-1'>
                                    <ul className='navbar-nav' id='navbar-nav'>
                                        <li className='nav-item '>
                                            <a
                                                className='nav-link menu-link text-black d-flex align-items-center '
                                                data-bs-toggle='collapse'
                                                href='#'
                                            >
                                                <i className='bx bx-file fs-4'></i>
                                                <span
                                                    data-key='t-apps fs-5'
                                                    style={{ marginLeft: 5 }}
                                                    className='d-none d-xxl-block'
                                                >
                                                    {typeOfPerson !==
                                                        'contractors' &&
                                                        langResources[
                                                            'header.reports'
                                                        ]}
                                                </span>

                                                <i className='ri-arrow-drop-down-line fs-4'></i>
                                            </a>

                                            <div
                                                id='sidebarApps'
                                                className='menu-dropdown collapse'
                                                style={{
                                                    right: 0,
                                                }}
                                            >
                                                <ul className='nav nav-sm flex-column test'>
                                                    <CostCenterMenuGroup
                                                        langResources={langResources}
                                                        handlerSaveReportByPeriod={
                                                            handlerSaveReportByPeriod
                                                        }
                                                    />
                                                    <BusinessUnitMenuGroup
                                                        endDateScheduling={
                                                            endDateScheduling
                                                        }
                                                        startDateScheduling={
                                                            startDateScheduling
                                                        }
                                                        selectedEmployees={
                                                            selectedEmployees
                                                        }
                                                    />

                                                    <OperationCenterMenuGroup
                                                        endDateScheduling={
                                                            endDateScheduling
                                                        }
                                                        startDateScheduling={
                                                            startDateScheduling
                                                        }
                                                        selectedEmployees={
                                                            selectedEmployees
                                                        }
                                                    />

                                                    <NoveltiesMenuGroup
                                                        endDateScheduling={
                                                            endDateScheduling
                                                        }
                                                        startDateScheduling={
                                                            startDateScheduling
                                                        }
                                                        selectedEmployees={
                                                            selectedEmployees
                                                        }
                                                    />

                                                    <MarkingMenuGroup
                                                        endDateScheduling={
                                                            endDateScheduling
                                                        }
                                                        startDateScheduling={
                                                            startDateScheduling
                                                        }
                                                        selectedEmployees={
                                                            selectedEmployees
                                                        }
                                                        setIsOpenFilterUnattendanceInterruptionsReportModal={
                                                            setIsOpenFilterUnattendanceInterruptionsReportModal
                                                        }
                                                        setIsOpenFilterDailyAssistance={
                                                            setIsOpenFilterDailyAssistance
                                                        }
                                                        setIsOpenFilterInterruptionsRepotModal={
                                                            setIsOpenFilterInterruptionsRepotModal
                                                        }
                                                        setIsOpenFilterLunchTimeControl={
                                                            setIsOpenFilterLunchTimeControl
                                                        }
                                                        setIsOpenFilterWorkingSchedulingsRepotModal={
                                                            setIsOpenFilterWorkingSchedulingsRepotModal
                                                        }
                                                    />

                                                    <RequestMenuGroup
                                                        endDateScheduling={
                                                            endDateScheduling
                                                        }
                                                        startDateScheduling={
                                                            startDateScheduling
                                                        }
                                                        selectedEmployees={
                                                            selectedEmployees
                                                        }
                                                    />

                                                    <WorkshiftMenuGroup
                                                        handlerSaveReportByPeriod={
                                                            handlerSaveReportByPeriod
                                                        }
                                                        endDateScheduling={
                                                            endDateScheduling
                                                        }
                                                        startDateScheduling={
                                                            startDateScheduling
                                                        }
                                                        selectedEmployees={
                                                            selectedEmployees
                                                        }
                                                    />

                                                    <PreliquidationsMenuGroup
                                                        endDateScheduling={
                                                            endDateScheduling
                                                        }
                                                        startDateScheduling={
                                                            startDateScheduling
                                                        }
                                                        selectedEmployees={
                                                            selectedEmployees
                                                        }
                                                    />
                                                </ul>
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </li>
            </ul>
        </>
    );
};

export default MenuBar;
